import { toRaw } from 'vue';
import { merge } from 'lodash';
import chroma from 'chroma-js';
import { Circle, Fill, RegularShape, Stroke, Style, Text } from 'ol/style';
import type {
  VectorLayerZoneStyle,
  ColorMap,
  VectorLayerZoneStyleData,
  VectorMapLayer,
  VectorLayerZone
} from '../models';
import { defaultZoneStyle } from '../config';
import { groupNumberByThree, integerPartOfNumber, roundToTenth } from '@core';
import { LineString, MultiLineString, MultiPoint, Point } from 'ol/geom';
import type Geometry from 'ol/geom/Geometry';
import type { Coordinate } from 'ol/coordinate';
import { ColorMapInterpolation } from '../enums';

export const applyFontSizeDependingOnResolution = (style: Style, clonedStyle: Style, resolution: number) => {
  const text = style.getText() as Text;
  if (!text) return;

  const font = text.getFont();
  if (!font) return;

  const replacer = (_: any, p1: any, p2: any, p3: any) => {
    const fontSize = Number(p2);
    const newSize = Math.min(Math.round((fontSize / resolution) * 2.5), fontSize * 1.5);

    return `${p1}${newSize}${p3}`;
  };

  const newFont = font.replace(/^(.+\s)(\d+)(px\/\d+\s".+")$/, replacer);

  clonedStyle.getText()?.setFont(newFont);

  if (resolution > 5) {
    clonedStyle.getText()?.getFill()?.setColor('transparent');
    clonedStyle.getText()?.getBackgroundFill()?.setColor('transparent');
    clonedStyle.getText()?.getStroke()?.setColor('transparent');
  }
};

export const generateFeatureStyle = (zoneStyle: Partial<VectorLayerZoneStyle>, title?: string) => {
  const styleProps = merge({}, defaultZoneStyle, zoneStyle);

  const style = new Style({
    fill: new Fill({
      color: styleProps.color
    }),
    stroke: new Stroke({
      color: styleProps.strokeColor,
      width: styleProps.strokeWidth
    })
  });

  if (title) {
    style.setText(
      new Text({
        font: `${styleProps.fontWeight} ${styleProps.fontSize}/${styleProps.fontLineHeight} "${styleProps.fontFamily}"`,
        text: title,
        overflow: styleProps.textOverflow,
        padding: styleProps.textPadding,
        fill: new Fill({
          color: styleProps.textColor
        }),
        backgroundFill: new Fill({
          color: styleProps.textBackgroundColor
        }),
        stroke: new Stroke({
          color: styleProps.textStrokeColor,
          width: styleProps.textStrokeWidth
        })
      })
    );
  }

  return style;
};

export const dotStyleFn = (zoneStyle: Partial<VectorLayerZoneStyle>) => {
  const styleProps = merge({}, defaultZoneStyle, zoneStyle);

  const lineStyle = new Style({
    stroke: new Stroke({
      color: styleProps.color,
      width: styleProps.strokeWidth
    })
  });

  const dotStyle = new Style({
    image: new Circle({
      radius: styleProps.pointRadius,
      fill: new Fill({ color: styleProps.color })
    }),
    geometry: (feature) => {
      const geom = feature.getGeometry();

      if (!geom) return undefined;

      const type = geom.getType();
      if (type === 'MultiLineString') {
        const lines = (geom as MultiLineString).getCoordinates();
        const coordinates = lines.flat();
        return new MultiPoint(coordinates);
      }

      return undefined;
    }
  });

  return (_) => [lineStyle, dotStyle];
};

const getLineCoordinates = (geometry: Geometry): Coordinate[][] => {
  const type = geometry.getType();

  if (type === 'MultiLineString') {
    return (geometry as MultiLineString).getCoordinates();
  }

  if (type === 'LineString') {
    return [(geometry as LineString).getCoordinates()];
  }

  return [];
};

export const arrowStyleFn = (zoneStyle: Partial<VectorLayerZoneStyle>) => {
  const styleProps = merge({}, defaultZoneStyle, zoneStyle);

  const lineStyle = new Style({
    stroke: new Stroke({
      color: styleProps.color,
      width: styleProps.strokeWidth
    })
  });

  const createArrowStyle = (coordinate: Coordinate, rotation: number) =>
    new Style({
      geometry: new Point(coordinate),
      image: new RegularShape({
        points: 3,
        radius: styleProps.arrowRadius,
        rotation,
        rotateWithView: true,
        fill: new Fill({ color: styleProps.color })
      })
    });

  return (feature) => {
    const geometry = feature.getGeometry();

    if (!geometry) return [lineStyle];

    const arrowStyles: Style[] = [];

    getLineCoordinates(geometry).forEach((line) => {
      line.forEach((coordinate, index) => {
        const nextCoordinate = line[index + 1];

        if (!nextCoordinate) return;

        const deltaX = nextCoordinate[0] - coordinate[0];
        const deltaY = nextCoordinate[1] - coordinate[1];

        if (deltaX === 0 && deltaY === 0) return;

        const midPoint: Coordinate = [coordinate[0] + deltaX / 2, coordinate[1] + deltaY / 2];

        arrowStyles.push(createArrowStyle(midPoint, Math.atan2(deltaX, deltaY)));
      });
    });

    return [lineStyle, ...arrowStyles];
  };
};

export const emptyZoneLabelFn = () => {
  return '';
};

export const valueZoneLabelFn = (_: VectorMapLayer, zone: VectorLayerZone) => {
  return zone.value !== undefined ? `${zone.value}` : '';
};

export const currencyValueZoneLabelFn = (_: VectorMapLayer, zone: VectorLayerZone) => {
  return zone.value !== undefined ? `${groupNumberByThree(zone.value.toFixed(0))}` : '';
};

export const tenthRoundedValueZoneLabelFn = (layer: VectorMapLayer, zone: VectorLayerZone) => {
  return `${roundToTenth(zone.value).toString()}${layer.data.unitType ? ` ${layer.data.unitType}` : ''}`;
};

export const decisionSupportYieldValueZoneLabelFn = (layer: VectorMapLayer, zone: VectorLayerZone) => {
  return `${(Number(zone.value) / 1000).toFixed(2)} ${layer.data.unitType ? ` ${layer.data.unitType}` : ''}`;
};

export const defaultStatisticsValueZoneLabelFn = (layer: VectorMapLayer, zone: VectorLayerZone) => {
  if (zone.value === undefined) {
    return '';
  }

  const numberValue = Number(zone.value);
  if (isNaN(numberValue)) {
    return zone.value !== undefined ? `${zone.value}` : '';
  } else {
    return `${Math.round(numberValue).toString()}${layer.data.unitType ? ` ${layer.data.unitType}` : ''}`;
  }
};

export const valueUnitZoneLabelFn = (layer: VectorMapLayer, zone: VectorLayerZone) => {
  const label = zone.value !== undefined ? `${zone.value}` : '';
  return layer.data.unitType ? `${label} ${layer.data.unitType}` : `${label}`;
};

export const integerValueUnitZoneLabelFn = (layer: VectorMapLayer, zone: VectorLayerZone) => {
  const label = zone.value !== undefined ? `${integerPartOfNumber(zone.value)}` : '';
  return layer.data.unitType ? `${label} ${layer.data.unitType}` : `${label}`;
};

export const nameZoneLabelFn = (_: VectorMapLayer, zone: VectorLayerZone) => {
  return zone.name;
};

export const defaultDotLineStyleFn = (layer: VectorMapLayer, _: number[]) => {
  return dotStyleFn(layer.data.zoneStyleConfig.defaultStyle);
};

export const defaultArrowLineStyleFn = (layer: VectorMapLayer, _: number[]) => {
  return arrowStyleFn(layer.data.zoneStyleConfig.defaultStyle);
};

export const defaultZoneStyleFn = async (layer: VectorMapLayer, values: number[]) => {
  const result: VectorLayerZoneStyleData[] = [];
  const colorMap = layer.data.colorMap;

  if (colorMap) {
    const { min, max, breaks } = await calculateColorVectorInfo(values, colorMap);

    layer.data.zones.forEach((zone) => {
      const defaultStyle =
        layer.data.classification === 'zone'
          ? generateZoneClassificationStyle(
              layer.data.zoneStyleConfig.defaultStyle,
              zone.value,
              colorMap,
              breaks,
              layer.data.zoneLabelFn(layer, zone)
            )
          : generateWorkClassificationStyle(
              layer.data.zoneStyleConfig.defaultStyle,
              zone.value,
              colorMap,
              min,
              max,
              layer.data.zoneLabelFn(layer, zone)
            );

      const selectedStyle =
        layer.data.classification === 'zone'
          ? generateZoneClassificationStyle(
              layer.data.zoneStyleConfig.selectedStyle,
              zone.value,
              colorMap,
              breaks,
              layer.data.zoneLabelFn(layer, zone)
            )
          : generateWorkClassificationStyle(
              layer.data.zoneStyleConfig.selectedStyle,
              zone.value,
              colorMap,
              min,
              max,
              layer.data.zoneLabelFn(layer, zone)
            );

      result.push({
        defaultStyle,
        selectedStyle
      });
    });
  } else {
    layer.data.zones.forEach((zone) => {
      result.push({
        defaultStyle: generateFeatureStyle(
          layer.data.zoneStyleConfig.defaultStyle,
          layer.data.zoneLabelFn(layer, zone)
        ),
        selectedStyle: generateFeatureStyle(
          layer.data.zoneStyleConfig.selectedStyle,
          layer.data.zoneLabelFn(layer, zone)
        )
      });
    });
  }

  return result;
};

export const calculateColorVectorInfo = async (values: number[], colorMap: ColorMap) => {
  let min = Math.min(...values);
  let max = Math.max(...values);
  const colors = colorMap.colors;

  // calculate breaks
  let breaks = toRaw(colorMap.breaks || []);

  if (!breaks.length) {
    const blockSize = (max - min) / colors.length;
    breaks = [min];

    for (let i = 1; i < colors.length - 1; i++) {
      breaks.push(min + blockSize * (i + 1));
    }

    breaks.push(max);
  } else {
    min = Math.min(...breaks);
    max = Math.max(...breaks);
  }

  // calculate color breaks
  const colorBreaks: { color: string; value: number; title?: string; percent: number }[] = [];
  for (let i = 0; i < breaks.length; i++) {
    const maybeTitle = colorMap.titles && colorMap.titles.length > i ? colorMap.titles[i] : undefined;

    colorBreaks.push({
      color: colors[i],
      value: breaks[i],
      percent: ((breaks[i] - min) / (max - min)) * 100,
      title: maybeTitle
    });
  }

  return {
    min,
    max,
    breaks,
    colorBreaks
  };
};

export const generateZoneClassificationStyle = (
  zoneStyle: Partial<VectorLayerZoneStyle>,
  zoneValue: number,
  colorMap: ColorMap,
  colorBreaks: number[],
  title?: string
) => {
  const colors = cleanColors(colorMap);
  const colorScale = chroma.scale(colors).domain(colorBreaks);

  return generateZoneStyle(zoneStyle, zoneValue, colorMap, colorScale, title);
};

export const generateZoneDiscreteClassificationStyle = (
  zoneStyle: Partial<VectorLayerZoneStyle>,
  zoneValue: number,
  colorMap: ColorMap,
  colorBreaks: number[],
  title?: string
) => {
  const colors = colorMap.colors;

  if (colorBreaks.length === 3 && colors.length === 3) {
    const zoneColor = colorBreaks[1] < zoneValue ? colors[2] : colorBreaks[1] > zoneValue ? colors[0] : colors[1];
    const styleConfig: Partial<VectorLayerZoneStyle> = merge(zoneStyle, {
      color: zoneColor,
      strokeColor: colorMap.strokeColor
    });

    return generateFeatureStyle(styleConfig, title);
  }

  return generateZoneClassificationStyle(zoneStyle, zoneValue, colorMap, colorBreaks, title);
};

const generateWorkClassificationStyle = (
  zoneStyle: Partial<VectorLayerZoneStyle>,
  zoneValue: number,
  colorMap: ColorMap,
  min: number,
  max: number,
  title?: string
) => {
  const colors = cleanColors(colorMap);
  const colorScale = chroma.scale(colors).domain([min, max]);

  return generateZoneStyle(zoneStyle, zoneValue, colorMap, colorScale, title);
};

const generateZoneStyle = (
  zoneStyle: Partial<VectorLayerZoneStyle>,
  zoneValue: number,
  colorMap: ColorMap,
  colorScale: chroma.Scale<chroma.Color>,
  title?: string
) => {
  const styleConfig: Partial<VectorLayerZoneStyle> = merge(zoneStyle, {
    color: colorScale(zoneValue).toString(),
    strokeColor: colorMap.strokeColor
  });

  return generateFeatureStyle(styleConfig, title);
};

export const cleanColors = (colorMap: ColorMap) => {
  return colorMap.colors.map((color) => color.replace('#', ''));
};

/**
 * Removes the alpha channel from a hex color string.
 * @param hexColor - Hex color string (e.g., '#FF0000A2' or '#FF0000')
 * @returns Hex color without alpha channel (e.g., '#FF0000')
 */
export const removeAlphaFromHex = (hexColor: string) => {
  return hexColor.length === 9 ? `#${hexColor.substring(1, 7)}` : hexColor;
};

/**
 * Adds an alpha channel to a hex color string.
 * Used by `createZoneStylesWithAlphaInterpolation` to make default zone colors semi-transparent.
 * @param hexColor - Hex color string (e.g., '#FF0000')
 * @param alpha - Alpha value to append (default: 'A2' for ~63% opacity)
 * @returns Hex color with alpha channel (e.g., '#FF0000A2')
 */
export const addAlphaToHex = (hexColor: string, alpha = 'A2') => {
  const cleanHex = removeAlphaFromHex(hexColor);
  return `${cleanHex}${alpha}`;
};

export const defaultZoneStylesWithAlphaFn = async (layer: VectorMapLayer, values: number[]) => {
  const colorMap = layer.data.colorMap;
  let breaks: number[] = [];
  const isDiscrete = colorMap?.interpolation === ColorMapInterpolation.DISCRETE;

  if (colorMap) {
    const colorInfo = await calculateColorVectorInfo(values, colorMap);
    breaks = colorInfo.breaks;
  }

  return createZoneStylesWithAlphaInterpolation(layer, breaks, isDiscrete);
};

/**
 * Creates zone styles with alpha channel manipulation: adds alpha (e.g., 'A2') to default colors for semi-transparency,
 * removes alpha from selected colors for full opacity. Unlike `defaultZoneStyleFn`, this manipulates alpha channels.
 * @param layer - The vector map layer containing zones to style
 * @param breaks - Color break points for classification (e.g., [0, 50, 100])
 * @param discrete - If true, uses discrete classification (optimized for 3-color, 3-break scenarios); if false, continuous interpolation
 * @returns Promise resolving to an array of zone style data (default and selected styles for each zone)
 */
export const createZoneStylesWithAlphaInterpolation = async (
  layer: VectorMapLayer,
  breaks: number[],
  discrete = false
) => {
  const result: VectorLayerZoneStyleData[] = [];
  const colorMap = layer.data.colorMap;

  if (colorMap) {
    const selectedColors = colorMap.colors.map((color) => removeAlphaFromHex(color));
    const defaultColors = colorMap.colors.map((color) => addAlphaToHex(color));
    const generationStyleFunction = discrete
      ? generateZoneDiscreteClassificationStyle
      : generateZoneClassificationStyle;

    layer.data.zones.forEach((zone) => {
      const defaultStyle = generationStyleFunction(
        layer.data.zoneStyleConfig.defaultStyle,
        zone.value,
        { ...colorMap, colors: defaultColors },
        breaks,
        layer.data.zoneLabelFn(layer, zone)
      );

      const selectedStyle = generationStyleFunction(
        layer.data.zoneStyleConfig.selectedStyle,
        zone.value,
        { ...colorMap, colors: selectedColors },
        breaks,
        layer.data.zoneLabelFn(layer, zone)
      );

      result.push({
        defaultStyle,
        selectedStyle
      });
    });
  } else {
    layer.data.zones.forEach((zone) => {
      result.push({
        defaultStyle: generateFeatureStyle(
          layer.data.zoneStyleConfig.defaultStyle,
          layer.data.zoneLabelFn(layer, zone)
        ),
        selectedStyle: generateFeatureStyle(
          layer.data.zoneStyleConfig.selectedStyle,
          layer.data.zoneLabelFn(layer, zone)
        )
      });
    });
  }

  return result;
};
